export interface User {
  id: number
  email: string
  name: string
  permissions: string[]
}

export type Permission =
  | 'can_annotate'
  | 'can_chat'
  | 'can_analyze_videos'
  | 'can_manage_videos'
  | 'can_manage_users'
  | 'can_view_graph'
  | 'can_all'

export function hasPermission(user: User | null, permission: Permission): boolean {
  if (!user) return false
  return user.permissions.includes('can_all') || user.permissions.includes(permission)
}

export type MentionSource = 'chapter' | 'transcript' | 'detected'

export interface Mention {
  id: number
  videoId: string
  timecode: number
  usedName: string
  source?: MentionSource
}

export type EntityType = 'person' | 'event' | 'topic' | 'other'

export interface EntityBase {
  id: number
  name: string
  description: string
  mentions: Mention[]
}

export interface TypedEntity extends EntityBase {
  entityType: EntityType
}

export interface Person {
  id: number
  name: string
  aliases: string[]
  description: string
  mentions: Mention[]
  video_count?: number
  internalOnly?: boolean
}

export interface HistoricalDate {
  year: number
  month?: number
  day?: number
}

export interface Event {
  id: number
  name: string
  description: string
  dateStart: HistoricalDate
  dateEnd?: HistoricalDate
  participants: string[]
  mentions: Mention[]
  isPersonalEvent: boolean
  video_count?: number
  internalOnly?: boolean
}

export interface TranscriptSnippet {
  text: string
  start: number
  duration: number
}

export interface Chapter {
  title: string
  start: number
}

export interface Topic {
  id: number
  name: string
  aliases: string[]
  description: string
  mentions: Mention[]
  isAdvertisement?: boolean
  video_count?: number
  internalOnly?: boolean
}

export interface Other {
  id: number
  name: string
  aliases: string[]
  description: string
  mentions: Mention[]
  founding?: HistoricalDate
  video_count?: number
  internalOnly?: boolean
}

export interface CrossVideoMention {
  timecode: number
  used_name: string
}

export interface CrossVideoEntry {
  youtube_id: string
  description: string | null
  duration: number
  mention_count: number
  snippet_text: string | null
  snippet_start: number | null
  mentions: CrossVideoMention[]
}

export interface StructuredData {
  snippets?: TranscriptSnippet[]
  chapters?: Chapter[]
}

export type AnalysisJobStatus = 'pending' | 'processing' | 'completed' | 'failed'

export interface AnalysisJob {
  id: number
  video_id: string
  status: AnalysisJobStatus
  progress: number
  current_stage: string | null
  error_message: string | null
  started_at: string | null
  completed_at: string | null
}

export interface AutoAnalyze {
  enabled: boolean
  channel_known: boolean
  channel_name: string | null
}

export interface VideoData {
  youtube_id: string
  transcript: string | null
  description: string | null
  structured_data: StructuredData | null
  estimated_time?: number
  analyzed?: boolean
  analysis_job?: AnalysisJob | null
  auto_analyze?: AutoAnalyze
  persons: Person[]
  events: Event[]
  topics: Topic[]
  others: Other[]
  transcript_error?: string
  transcript_error_message?: string
}

export type TypedPerson = Person & { entityType: 'person' }
export type TypedEvent = Event & { entityType: 'event' }
export type TypedTopic = Topic & { entityType: 'topic' }
export type TypedOther = Other & { entityType: 'other' }
export type AnyTypedEntity = TypedPerson | TypedEvent | TypedTopic | TypedOther

export function toTypedEntities(videoData: VideoData): AnyTypedEntity[] {
  const persons: TypedPerson[] = videoData.persons.map(p => ({ ...p, entityType: 'person' as const }))
  const events: TypedEvent[] = videoData.events.map(e => ({ ...e, entityType: 'event' as const }))
  const topics: TypedTopic[] = videoData.topics.map(t => ({ ...t, entityType: 'topic' as const }))
  const others: TypedOther[] = (videoData.others || []).map(o => ({ ...o, entityType: 'other' as const }))
  return [...persons, ...events, ...topics, ...others]
}

export function getFirstMentionTime(entity: EntityBase): number {
  if (!entity.mentions || entity.mentions.length === 0) return Infinity
  return Math.min(...entity.mentions.map(m => m.timecode))
}

export function sortEntitiesByFirstMention<T extends EntityBase>(entities: T[]): T[] {
  return [...entities].sort((a, b) => getFirstMentionTime(a) - getFirstMentionTime(b))
}
